import { Button, Modal, Popconfirm, Table, message } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useCallback, useMemo, useState } from 'react'
import dayjs from '../../lib/datetime'
import api from '../../api'
import { useAsync } from '../../hooks'
import { getPaginationHeaders } from '../../redux/utils'
import { manufacturerLabel } from '../../lib/companies'

function formatDate(value) {
  return value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—'
}

export default function ConnectivityAlerts() {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [resolvingId, setResolvingId] = useState(null)

  const fetchAlerts = useCallback(
    () => api.transmissions.connectivityAlerts({ page, perPage: pageSize }),
    [page, pageSize],
  )

  const { value, pending, execute } = useAsync(fetchAlerts)
  const rows = value?.body || []
  const { total = 0 } = value ? getPaginationHeaders(value.headers) : {}

  const handlePageChange = useCallback((nextPage, nextPageSize) => {
    setPage(nextPage)
    setPageSize(nextPageSize)
  }, [])

  const handleResolve = useCallback(
    async id => {
      setResolvingId(id)
      try {
        await api.transmissions.resolveConnectivityAlert(id)
        message.success('Alert resolved')
        await execute()
      } catch (error) {
        message.error(error.message || 'Could not resolve the alert')
      } finally {
        setResolvingId(null)
      }
    },
    [execute],
  )

  const columns = useMemo(
    () => [
      {
        title: 'Practice',
        dataIndex: 'practiceName',
        key: 'practiceName',
      },
      {
        title: 'Manufacturer',
        dataIndex: 'manufacturer',
        key: 'manufacturer',
        render: manufacturerLabel,
      },
      {
        title: 'Last Received',
        dataIndex: 'lastTransmissionAt',
        key: 'lastTransmissionAt',
        render: formatDate,
      },
      {
        title: 'Triggered',
        dataIndex: 'triggeredAt',
        key: 'triggeredAt',
        render: formatDate,
      },
      {
        title: '',
        key: 'actions',
        align: 'right',
        render(_, alert) {
          return (
            <Popconfirm
              title="Resolve this alert?"
              okText="Resolve"
              onConfirm={() => handleResolve(alert.id)}
            >
              <Button size="small" loading={resolvingId === alert.id}>
                Resolve
              </Button>
            </Popconfirm>
          )
        },
      },
    ],
    [handleResolve, resolvingId],
  )

  return (
    <>
      <Button
        type="link"
        icon={<SearchOutlined />}
        danger={total > 0}
        disabled={total === 0}
        onClick={() => setOpen(true)}
        style={{ fontWeight: 600 }}
      >
        {total} unresolved {total === 1 ? 'alert' : 'alerts'}
      </Button>
      <Modal
        title="Unresolved connectivity alerts"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
      >
        <Table
          columns={columns}
          dataSource={rows}
          loading={pending}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: handlePageChange,
          }}
        />
      </Modal>
    </>
  )
}
